import { useEffect, useState } from "react";
import { Input, Card, Collapse, Typography } from "antd";
import { toast } from "react-hot-toast";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../interceptors/api";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

export default function UrlShortener() {
  const { t } = useTranslation();
  const [originalUrl, setOriginalUrl] = useState("");
  const [shortUrl, setShortUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [urls, setUrls] = useState([]);

  const fetchUrls = async () => {
    try {
      const res = await api.get("/short-urls");
      setUrls(res.data.urls || []);
    } catch (err) {
      toast.error(t("failed_to_load_urls"));
    }
  };

  useEffect(() => {
    fetchUrls();
  }, []);

  const handleShorten = async () => {
    if (!originalUrl.trim()) return toast.error(t("please_enter_url"));
    setLoading(true);
    try {
      const res = await api.post("/short-urls", { originalUrl });
      setShortUrl(res.data.shortUrl);
      toast.success(t("url_shortened_successfully"));
      fetchUrls();
    } catch (err) {
      toast.error(err.response?.data?.error || t("api_request_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-0">
      <Header />
      <main>
        <Card className="max-w-5xl mx-auto p-6 space-y-6">
          <div className="space-y-4">
            <Input.TextArea
              placeholder={t("url_shortener_input_placeholder")}
              value={originalUrl}
              onChange={(e) => setOriginalUrl(e.target.value)}
              size="large"
              autoSize={{ minRows: 2, maxRows: 5 }}
            />
            <StandardButton
              onClick={handleShorten}
              loading={loading}
              type="secondary"
              size="large"
            >
              {t("shorten_button")}
            </StandardButton>

            {shortUrl && (
              <div className="mt-4">
                <p className="font-semibold mb-1">{t("shortened_url")}</p>
                <a
                  href={shortUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 hover:underline break-all"
                >
                  {shortUrl}
                </a>
              </div>
            )}
          </div>

          {urls.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-semibold mb-4">{t("url_all")}</h3>
              <Collapse accordion>
                {urls.map((item, index) => (
                  <Collapse.Panel
                    key={index}
                    header={
                      <div className="flex flex-col">
                        <Typography.Text strong className="text-base">
                          {item.original_url.length > 60
                            ? item.original_url.slice(0, 60) + "..."
                            : item.original_url}
                        </Typography.Text>
                        <span className="text-xs text-gray-500">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      </div>
                    }
                  >
                    <div className="bg-gray-50 p-3 rounded text-sm space-y-2">
                      <div className="break-words">
                        <strong>{t("original_url")}:</strong> {item.original_url}
                      </div>
                      <div className="break-all">
                        <strong>{t("shortened_url")}:</strong>{" "}
                        <a
                          href={`${window.location.origin}/r/${item.slug}`}
                          className="text-indigo-600 hover:underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {`${window.location.origin}/r/${item.slug}`}
                        </a>
                      </div>
                    </div>
                  </Collapse.Panel>
                ))}
              </Collapse>
            </div>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
}